import React, { useState } from 'react';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, CircularProgress, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Chip } from '@mui/material';
import SchoolFormModal from '../../../components/Modals/SchoolFormModal';
import { useSchools,SchoolData } from '../../../hooks/useSchools';
import { broadcastAdminRevocation, broadcastSchoolDeletion, clearAdminCache, triggerImmediatePollWithRefresh } from 'utils/forceLogoutGuard';
import { useTranslation } from 'react-i18next';
import EnhancedTable from '../../../components/common/EnhancedTable';
import ActionButtons from '../../../components/common/ActionButtons';
import { useQueryClient } from '@tanstack/react-query';
import { useError } from '../../../contexts/ErrorContext';
import { getSchoolFormDraft, clearSchoolFormDraft } from 'utils/storage';

const SchoolManagement: React.FC = () => {
  const schoolHooks = useSchools(false, { enableQuery: true, disableSchoolAdmins: false });
  const {
    schoolsQuery,
    createSchoolMutation,
    updateSchoolMutation,
    deleteSchoolMutation,
    schoolAdmins,
    refetchSchoolAdmins,
  } = schoolHooks;
  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const { showSuccess, showError } = useError();

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentSchool, setCurrentSchool] = useState<SchoolData | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState<number | null>(null);
  const [localSchools, setLocalSchools] = useState<SchoolData[]>([]);
  const [restoreDraftDialogOpen, setRestoreDraftDialogOpen] = useState(false);
  const [draftData, setDraftData] = useState<SchoolData | null>(null);

  // Update local schools when query data changes
  React.useEffect(() => {
    if (schoolsQuery.data) {
      setLocalSchools(schoolsQuery.data);
    }
  }, [schoolsQuery.data]);



  // Check if any mutation is in progress
  const isCreating = createSchoolMutation.isLoading;
  const isUpdating = updateSchoolMutation.isLoading;
  const isDeleting = deleteSchoolMutation.isLoading;
  const isLoading = isCreating || isUpdating || isDeleting;

  const handleOpenAddModal = () => {
    setIsEdit(false);

    // Check if there's saved draft data
    const savedDraft = getSchoolFormDraft();
    if (savedDraft) {
      setDraftData(savedDraft);
      setRestoreDraftDialogOpen(true);
    } else {
      setCurrentSchool(undefined);
      // Refetch school admins to get the latest list
      refetchSchoolAdmins();
      setModalOpen(true);
    }
  };

  const handleRestoreDraft = () => {
    if (draftData) {
      setCurrentSchool(draftData);
      setRestoreDraftDialogOpen(false);
      // Refetch school admins to get the latest list
      refetchSchoolAdmins();
      setModalOpen(true);
    }
  };

  const handleDiscardDraft = () => {
    clearSchoolFormDraft();
    setDraftData(null);
    setRestoreDraftDialogOpen(false);
    setCurrentSchool(undefined);
    // Refetch school admins to get the latest list
    refetchSchoolAdmins();
    setModalOpen(true);
  };

  const handleOpenEditModal = (school: SchoolData) => {
    setIsEdit(true);
    setCurrentSchool(school);
    // Refetch school admins to get the latest list
    refetchSchoolAdmins();
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    // Note: Draft is saved in SchoolFormModal when cancel is clicked or modal is closed
    // We don't clear it here - it will be cleared on successful creation or when user chooses to discard
  };

  const handleSubmit = (formData: SchoolData, imageFile?: File, schoolImageFile?: File) => {
    const payload = {
      ...formData,
      schoolLogo: imageFile ? imageFile.name : "",
      schoolImage: schoolImageFile ? schoolImageFile.name : "",
    };
    if (isEdit && currentSchool?.id) {
      const previousAdminIds = currentSchool?.schoolAdminIds || [];
      const nextAdminIds = payload.schoolAdminIds || [];
      const removedAdmins = previousAdminIds.filter(
        (adminId) => !nextAdminIds.includes(adminId)
      );
      updateSchoolMutation.mutate(
        { id: currentSchool.id, schoolData: payload, imageFile, schoolImageFile },
        {
          onSuccess: () => {
            showSuccess(t('school_updated_successfully') || 'School updated successfully');
            if (removedAdmins.length > 0) {
              // Broadcast logout to affected admins FIRST (triggers immediate logout for same-browser tabs)
              // This checks if current user is in removed list and logs out immediately
              broadcastAdminRevocation(removedAdmins);

              // Clear cache and trigger immediate poll check after a short delay
              // This ensures users in other browsers/tabs are logged out quickly (within 1 second)
              setTimeout(() => {
                clearAdminCache();
                triggerImmediatePollWithRefresh();
              }, 500); // 500ms delay to ensure backend has processed the update
            } else {
              // Even if no admins were removed, clear cache to ensure fresh data on next poll
              clearAdminCache();
            }
            handleCloseModal();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || '';
            const errorLower = errorMessage.toLowerCase();

            // Check for duplicate dice code and school code errors - handle various formats
            const hasDiceCodeError = errorLower.includes('dice') || errorLower.includes('dise');
            const hasSchoolCodeError = errorLower.includes('school code') || errorLower.includes('schoolcode') ||
              (errorLower.includes('school') && errorLower.includes('code') && !errorLower.includes('dice') && !errorLower.includes('dise'));

            if (hasDiceCodeError && hasSchoolCodeError) {
              showError(t('school_with_dice_code_and_school_code_already_exists') || 'School with Dice code and School code already exists');
            } else if (hasDiceCodeError) {
              showError(t('school_with_dice_code_already_exists') || 'School with Dice code already exists');
            } else if (hasSchoolCodeError) {
              showError(t('school_with_school_code_already_exists') || 'School with School code already exists');
            } else if (errorMessage) {
              showError(errorMessage);
            } else {
              showError(t('error_updating_school') || 'Failed to update school. Please try again.');
            }
          },
        }
      );
    } else {
      // Close modal immediately when creating school
      handleCloseModal();

      createSchoolMutation.mutate(
        { schoolData: payload, imageFile, schoolImageFile },
        {
          onSuccess: () => {
            // Clear draft data on successful creation
            clearSchoolFormDraft();
            showSuccess(t('school_created_successfully') || 'School created successfully');
          },
          onError: (error: any) => {
            // Draft data is already saved in SchoolFormModal's handleSubmit
            // So we don't need to save it again here

            const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || '';
            const errorLower = errorMessage.toLowerCase();

            // Check for duplicate dice code and school code errors - handle various formats
            const hasDiceCodeError = errorLower.includes('dice') || errorLower.includes('dise');
            const hasSchoolCodeError = errorLower.includes('school code') || errorLower.includes('schoolcode') ||
              (errorLower.includes('school') && errorLower.includes('code') && !errorLower.includes('dice') && !errorLower.includes('dise'));

            if (hasDiceCodeError && hasSchoolCodeError) {
              showError(t('school_with_dice_code_and_school_code_already_exists') || 'School with Dice code and School code already exists');
            } else if (hasDiceCodeError) {
              showError(t('school_with_dice_code_already_exists') || 'School with Dice code already exists');
            } else if (hasSchoolCodeError) {
              showError(t('school_with_school_code_already_exists') || 'School with School code already exists');
            } else if (errorMessage) {
              showError(errorMessage);
            } else {
              showError(t('error_creating_school') || 'Failed to create school. Please try again.');
            }
          },
        }
      );
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    setSchoolToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSchoolToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (schoolToDelete !== null) {
      // Get school data before deletion to get admin IDs for logout
      const schoolToDeleteData = localSchools.find(school => school.id === schoolToDelete);

      // Immediately remove the school from local state for instant UI update
      setLocalSchools(prev => prev.filter(school => school.id !== schoolToDelete));

      deleteSchoolMutation.mutate(schoolToDelete, {
        onSuccess: () => {
          // Broadcast logout to all users of the deleted school
          // This will logout all admins, teachers, and students associated with this school
          broadcastSchoolDeletion(schoolToDelete);

          // Also logout any admins that were specifically assigned to this school
          if (schoolToDeleteData?.schoolAdminIds && schoolToDeleteData.schoolAdminIds.length > 0) {
            broadcastAdminRevocation(schoolToDeleteData.schoolAdminIds, "school-deleted");
          }

          // Clear cache to ensure fresh data
          clearAdminCache();

          // Trigger immediate poll check for affected users
          setTimeout(() => {
            triggerImmediatePollWithRefresh();
          }, 500);

          showSuccess(t('school_deleted_successfully') || 'School deleted successfully');

          // School is already removed from local state, just close dialog
          handleCloseDeleteDialog();
        },
        onError: (error: unknown) => {
          // If deletion fails, add the school back to the list
          const schoolToRestore = schoolsQuery.data?.find((school: SchoolData) => school.id === schoolToDelete);
          if (schoolToRestore) {
            setLocalSchools(prev => [...prev, schoolToRestore]);
          }
          showError(t('error_deleting_school') || 'Error deleting school');
          // Keep the dialog open on error so user can try again
          setDeleteDialogOpen(true);
        }
      });
    }
  };



  const columns = [
    { id: 'nameofSchool', label: t('school_name'), minWidth: 130 },
    { id: 'schoolCode', label: t('school_code'), minWidth: 100 },
    { id: 'diseCode', label: t('dise_code'), minWidth: 100 },
    { id: 'district', label: t('district'), minWidth: 100 },
    { id: 'state', label: t('state'), minWidth: 100 },
    { id: 'schoolPrincipalName', label: t('principal_name'), minWidth: 130 }
  ];

  const formattedData = (localSchools || []).filter(school => school.status !== false).map((row: any) => ({
    ...row,
  }));

  const renderActions = (row: any) => (
    <ActionButtons
      onEdit={() => handleOpenEditModal(row)}
      onDelete={() => handleOpenDeleteDialog(row.id)}
      editTooltip={t('edit')}
      deleteTooltip={t('delete')}
    />
  );

  return (
    <>
      <Paper sx={{ p: 2, width: '100%', maxWidth: 1500, mx: 'auto', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {t('school_management')}
          </Typography>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            onClick={handleOpenAddModal}
            disabled={isLoading}
          >
            {isCreating ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('creating_school')}
              </>
            ) : (
              t('add_school')
            )}
          </Button>
        </Box>

        {schoolsQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ mr: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t('loading_schools')}
            </Typography>
          </Box>
        )}
        {schoolsQuery.error instanceof Error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {schoolsQuery.error.message}
          </Alert>
        )}

        {!schoolsQuery.isLoading && (
          <EnhancedTable
            columns={columns}
            data={formattedData}
            searchFields={[
              'nameofSchool',
              'schoolCode',
              'diseCode',
              'district',
              'state',
              'schoolPrincipalName'
            ]}
            filterFields={[]}
            actions={renderActions}
            cardProps={{
              elevation: 0,
              sx: {
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent'
              }
            }}
          />
        )}
      </Paper>

      <SchoolFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentSchool}
        onSubmit={handleSubmit}
        isEdit={isEdit}
        schoolAdmins={schoolAdmins}
        refetchSchoolAdmins={refetchSchoolAdmins}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('confirm_delete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('confirm_delete_school')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            autoFocus
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('deleting')}
              </>
            ) : (
              t('delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Draft Dialog */}
      <Dialog
        open={restoreDraftDialogOpen}
        onClose={handleDiscardDraft}
        aria-labelledby="restore-draft-dialog-title"
        aria-describedby="restore-draft-dialog-description"
      >
        <DialogTitle id="restore-draft-dialog-title">
          {t('restore_previous_data') || 'Restore Previous Data?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="restore-draft-dialog-description">
            {t('restore_draft_message') || 'We found previously filled form data. Would you like to restore it?'}
            <br />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('restore_draft_note') || 'Note: Uploaded images will need to be re-uploaded.'}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDiscardDraft}
            variant="outlined"
            sx={{ borderColor: "#666", color: "#666" }}
          >
            {t('no_start_fresh') || 'No, Start Fresh'}
          </Button>
          <Button
            onClick={handleRestoreDraft}
            variant="contained"
            autoFocus
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {t('yes_restore') || 'Yes, Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SchoolManagement;